import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageCircle, Send, Facebook, Twitter, Linkedin,
  CheckCircle, XCircle, AlertTriangle, Loader2,
  Plug, RefreshCw, ChevronRight, Shield, ArrowRight,
  Inbox, UserPlus, Ticket, Reply, Search, Brain,
  Sparkles, Eye, ExternalLink, TestTube, Trash2,
  MessagesSquare, Bot, ArrowUpRight, Settings2, FileText
} from 'lucide-react';
import api from '../lib/api';
import { WhatsAppEmbeddedSignup } from '../components/WhatsAppEmbeddedSignup';
import { WhatsAppTemplatesDialog } from '../components/WhatsAppTemplatesDialog';
import { MetaEmbeddedSignup } from '../components/MetaEmbeddedSignup';

// Backend base URL — used to display the webhook URL users paste into the platform.
const API = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORM_ICONS = {
  whatsapp: MessageCircle,
  telegram: Send,
  meta: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
};

const PLATFORM_COLORS = {
  whatsapp: 'text-green-500 bg-green-500/10',
  telegram: 'text-blue-500 bg-blue-500/10',
  meta: 'text-blue-600 bg-blue-600/10',
  twitter: 'text-sky-500 bg-sky-500/10',
  linkedin: 'text-blue-700 bg-blue-700/10',
};

// ─── Platform Card ───
function PlatformCard({ id, platform, connection, onSetup, onTest, onDisconnect, testing, onTemplates }) {
  const Icon = PLATFORM_ICONS[id] || Plug;
  const colors = PLATFORM_COLORS[id] || 'text-gray-500 bg-gray-500/10';
  const isConnected = connection?.is_connected;

  return (
    <Card className={`transition-all hover:shadow-md ${isConnected ? 'border-primary/20' : 'border-dashed'}`} data-testid={`platform-card-${id}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{platform.name}</h3>
              <p className="text-xs text-muted-foreground">{platform.description}</p>
            </div>
          </div>
          {isConnected ? (
            connection?.test_success ? (
              <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />Connected
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />Untested
              </Badge>
            )
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">Not Connected</Badge>
          )}
        </div>

        {/* Capabilities */}
        <div className="flex flex-wrap gap-1 mb-3">
          {platform.capabilities?.map(cap => (
            <span key={cap} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">
              {cap.replace('_', ' ')}
            </span>
          ))}
        </div>

        {isConnected && connection?.message_count > 0 && (
          <div className="text-xs text-muted-foreground mb-3">
            {connection.message_count} messages received
          </div>
        )}

        <div className="flex gap-2">
          <Button variant={isConnected ? 'outline' : 'default'} size="sm" className="flex-1 h-8 text-xs" onClick={() => onSetup(id)} data-testid={`setup-${id}-btn`}>
            {isConnected ? 'Update' : 'Connect'}<ChevronRight className="h-3 w-3 ml-1" />
          </Button>
          {isConnected && (
            <>
              {id === 'whatsapp' && (
                <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => onTemplates && onTemplates()} data-testid="whatsapp-templates-btn">
                  <FileText className="h-3 w-3 mr-1" />Templates
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={testing === id} onClick={() => onTest(id)} data-testid={`test-${id}-btn`}>
                {testing === id ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube className="h-3 w-3" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => onDisconnect(id)} data-testid={`disconnect-${id}-btn`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Setup Wizard ───
function SetupWizard({ platformId, platform, open, onClose, onSave, saving, onConnected }) {
  const [fields, setFields] = useState({});
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) { setFields({}); setStep(0); } }, [open]);

  if (!platform) return null;

  const isWhatsApp = platformId === 'whatsapp';
  const isMeta = platformId === 'meta';

  const activeFields = platform.setup_fields || [];
  const activeLabels = platform.setup_labels || {};
  const activeSteps = platform.setup_steps || [];
  const activeWebhook = platform.webhook_path;
  const requiredFields = activeFields;
  const secretFields = ['api_key', 'webhook_secret', 'access_token', 'app_secret', 'bot_token', 'api_secret', 'access_secret', 'page_access_token', 'verify_token'];

  const allFilled = requiredFields.every(f => fields[f]?.trim());

  const handleSave = () => {
    const payload = { ...fields };
    if (isWhatsApp) payload.integration_mode = 'cloud_api';
    onSave(platformId, payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="social-setup-wizard">
        <DialogHeader>
          <DialogTitle>Connect {platform.name}</DialogTitle>
          <DialogDescription>
            {step === 0 ? 'Follow these steps to get your credentials' : 'Enter your credentials'}
          </DialogDescription>
        </DialogHeader>

        {step === 0 ? (
          <div className="space-y-3 py-2">
            {isWhatsApp && (
              <div className="space-y-3">
                <WhatsAppEmbeddedSignup onConnected={() => { onClose(); onConnected && onConnected(); }} />
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or connect manually</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}
            {isMeta && (
              <div className="space-y-3">
                <MetaEmbeddedSignup onConnected={() => { onClose(); onConnected && onConnected(); }} />
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or connect manually</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}
            {activeSteps.map((s, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                <p className="text-sm text-muted-foreground">{s}</p>
              </div>
            ))}
            {activeWebhook && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border mt-3">
                <p className="text-xs font-medium mb-1">Webhook URL (paste in platform settings):</p>
                <code className="text-xs text-primary break-all" data-testid="wizard-webhook-url">{API}{activeWebhook}</code>
              </div>
            )}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <Shield className="h-4 w-4 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-600">Credentials are stored securely and never exposed.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {activeFields.map(field => (
              <div key={field} className="space-y-1">
                <label className="text-sm font-medium">
                  {activeLabels[field] || field}
                  {!requiredFields.includes(field) && <span className="text-muted-foreground font-normal"> (optional)</span>}
                </label>
                <Input
                  type={secretFields.includes(field) ? 'password' : 'text'}
                  value={fields[field] || ''}
                  onChange={(e) => setFields(prev => ({ ...prev, [field]: e.target.value }))}
                  data-testid={`wizard-${field}`}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {step === 0 ? (
            <Button onClick={() => setStep(1)} data-testid="wizard-next-btn">
              I have my credentials <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button className="flex-1" disabled={!allFilled || saving} onClick={handleSave} data-testid="wizard-connect-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Save & Connect
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Unified Inbox ───
function UnifiedInbox({ messages, total, onReply, onCreateLead, onCreateTicket, onRefresh, loading }) {
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const handleReply = async (msgId) => {
    if (!replyText.trim()) return;
    setSending(true);
    await onReply(msgId, replyText);
    setReplyId(null);
    setReplyText('');
    setSending(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{total} messages</p>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-inbox-btn">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {messages.length === 0 ? (
        <Card data-testid="inbox-empty">
          <CardContent className="py-10 text-center text-muted-foreground">
            <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Connect a platform and messages will appear here</p>
          </CardContent>
        </Card>
      ) : (
        messages.map((msg) => {
          const Icon = PLATFORM_ICONS[msg.platform] || MessageCircle;
          const colors = PLATFORM_COLORS[msg.platform] || 'text-gray-500 bg-gray-500/10';
          return (
            <Card key={msg.id} className={`${msg.status === 'new' ? 'border-primary/30' : ''}`} data-testid={`inbox-msg-${msg.id}`}>
              <CardContent className="py-3">
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${colors}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{msg.sender_name || 'Unknown'}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5">{msg.platform}</Badge>
                      {msg.status === 'new' && <Badge className="text-[10px] px-1.5 bg-primary">New</Badge>}
                      {msg.status === 'replied' && <Badge variant="secondary" className="text-[10px]">Replied</Badge>}
                      {msg.status === 'lead_created' && <Badge variant="outline" className="text-[10px] text-green-600">Lead</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{msg.message_text}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-[10px] text-muted-foreground">
                        {msg.received_at ? new Date(msg.received_at).toLocaleString() : ''}
                      </span>
                    </div>

                    {msg.reply_text && (
                      <div className="mt-2 p-2 rounded bg-primary/5 border border-primary/20 text-xs">
                        <span className="font-medium">Reply:</span> {msg.reply_text}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-1 mt-2">
                      {msg.status === 'new' && (
                        <>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setReplyId(msg.id); setReplyText(''); }} data-testid={`reply-btn-${msg.id}`}>
                            <Reply className="h-3 w-3 mr-1" />Reply
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onCreateLead(msg.id)} data-testid={`create-lead-btn-${msg.id}`}>
                            <UserPlus className="h-3 w-3 mr-1" />Lead
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onCreateTicket(msg.id)} data-testid={`create-ticket-btn-${msg.id}`}>
                            <Ticket className="h-3 w-3 mr-1" />Ticket
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Reply box */}
                    {replyId === msg.id && (
                      <div className="flex gap-2 mt-2">
                        <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply..." className="h-8 text-sm" data-testid={`reply-input-${msg.id}`} />
                        <Button size="sm" className="h-8" disabled={!replyText.trim() || sending} onClick={() => handleReply(msg.id)} data-testid={`send-reply-btn-${msg.id}`}>
                          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ─── Enrichment Panel ───
function EnrichmentPanel({ onEnrich, enrichments, loading }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [result, setResult] = useState(null);

  const handleEnrich = async () => {
    setEnriching(true);
    setResult(null);
    const data = await onEnrich({ name, email, company });
    setResult(data);
    setEnriching(false);
  };

  return (
    <div className="space-y-4">
      <Card data-testid="enrichment-form-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            AI Lead Enrichment
          </CardTitle>
          <CardDescription>Enter lead details and AI will generate social enrichment insights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="h-8" data-testid="enrich-name" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" className="h-8" data-testid="enrich-email" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Company</label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" className="h-8" data-testid="enrich-company" />
            </div>
          </div>
          <Button onClick={handleEnrich} disabled={enriching || (!name && !email && !company)} data-testid="enrich-btn">
            {enriching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Enrich with AI
          </Button>
        </CardContent>
      </Card>

      {/* Enrichment Result */}
      {result?.enrichment && (
        <Card className="border-primary/20" data-testid="enrichment-result-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-5 w-5 text-green-500" />
              Enrichment Result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Estimated Role</p>
                <p className="text-sm font-medium">{result.enrichment.estimated_role || '—'}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Industry</p>
                <p className="text-sm font-medium">{result.enrichment.company_industry || '—'}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Company Size</p>
                <p className="text-sm font-medium">{result.enrichment.company_size || '—'}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Lead Score</p>
                <p className="text-sm font-bold text-primary">{result.enrichment.lead_score || 0}/100</p>
              </div>
            </div>

            {result.enrichment.social_profiles && (
              <div className="mb-3">
                <p className="text-xs font-medium mb-2">Social Profiles</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.enrichment.social_profiles).map(([key, url]) => url && (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted/30 border text-xs hover:bg-muted/50">
                      <ExternalLink className="h-3 w-3" />{key.replace('_', ' ')}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {result.enrichment.talking_points?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium mb-2">Talking Points</p>
                <div className="space-y-1">
                  {result.enrichment.talking_points.map((tp, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span className="text-primary font-bold">{i + 1}.</span>
                      <span>{tp}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.enrichment.enrichment_notes && (
              <p className="text-xs text-muted-foreground italic">{result.enrichment.enrichment_notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Past Enrichments */}
      {enrichments.length > 0 && (
        <Card data-testid="enrichment-history-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recent Enrichments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {enrichments.slice(0, 10).map((e) => (
                <div key={e.id} className="flex items-center justify-between p-2 rounded bg-muted/20 border border-border/50 text-xs">
                  <div>
                    <span className="font-medium">{e.name || e.email || e.company}</span>
                    {e.enrichment?.lead_score && (
                      <Badge variant="outline" className="ml-2 text-[10px]">Score: {e.enrichment.lead_score}</Badge>
                    )}
                  </div>
                  <span className="text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Conversations Panel ───
function ConversationsPanel({ conversations, onSelect, onRefresh, loading, selectedConv, thread, threadLoading, onSendOutbound }) {
  const [outboundText, setOutboundText] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!outboundText.trim() || !selectedConv) return;
    setSending(true);
    await onSendOutbound(selectedConv.platform, selectedConv.contact_id, selectedConv.contact_name, outboundText);
    setOutboundText('');
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="conversations-panel">
      {/* Conversation List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium">{conversations.length} conversations</p>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-conversations-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {conversations.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">
            <MessagesSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No conversations yet</p>
          </CardContent></Card>
        ) : (
          conversations.map((c, i) => {
            const Icon = PLATFORM_ICONS[c.platform] || MessageCircle;
            const colors = PLATFORM_COLORS[c.platform] || 'text-gray-500 bg-gray-500/10';
            const isSelected = selectedConv?.contact_id === c.contact_id && selectedConv?.platform === c.platform;
            return (
              <div key={`${c.platform}-${c.contact_id}-${i}`}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/30'}`}
                onClick={() => onSelect(c)}
                data-testid={`conversation-item-${i}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${colors}`}><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{c.contact_name}</span>
                    <span className="text-[10px] text-muted-foreground">{c.message_count} msgs</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
                </div>
                {c.last_time && <span className="text-[10px] text-muted-foreground shrink-0">{new Date(c.last_time).toLocaleDateString()}</span>}
              </div>
            );
          })
        )}
      </div>

      {/* Thread View */}
      <div className="lg:col-span-2">
        {selectedConv ? (
          <Card data-testid="conversation-thread">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                {React.createElement(PLATFORM_ICONS[selectedConv.platform] || MessageCircle, { className: 'h-4 w-4' })}
                {selectedConv.contact_name}
                <Badge variant="outline" className="text-[10px]">{selectedConv.platform}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {threadLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto mb-3">
                  {thread.map((msg, i) => {
                    const isOutbound = msg.direction === 'outbound';
                    return (
                      <div key={msg.id || i} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${isOutbound ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          <p>{msg.message_text}</p>
                          <p className={`text-[10px] mt-1 ${isOutbound ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                            {msg.received_at || msg.sent_at ? new Date(msg.received_at || msg.sent_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2">
                <Input value={outboundText} onChange={(e) => setOutboundText(e.target.value)}
                  placeholder={`Send a message via ${selectedConv.platform}...`}
                  className="h-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  data-testid="outbound-message-input" />
                <Button size="sm" className="h-9 px-4" disabled={!outboundText.trim() || sending} onClick={handleSend} data-testid="send-outbound-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <MessagesSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>Select a conversation to view messages</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

// ─── Auto-Reply Panel ───
function AutoReplyPanel({ autoReplies, platforms, onSave, onDelete }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ reply_text: '', trigger_keywords: '', only_new_contacts: false });
  const [saving, setSaving] = useState(false);

  const replyPlatforms = Object.entries(platforms).filter(([, p]) => p.capabilities?.includes('reply'));

  const startEdit = (platformId) => {
    const existing = autoReplies[platformId];
    setForm({
      reply_text: existing?.reply_text || '',
      trigger_keywords: (existing?.trigger_keywords || []).join(', '),
      only_new_contacts: existing?.only_new_contacts || false,
    });
    setEditing(platformId);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    await onSave({
      platform: editing,
      enabled: true,
      reply_text: form.reply_text,
      trigger_keywords: form.trigger_keywords.split(',').map(s => s.trim()).filter(Boolean),
      only_new_contacts: form.only_new_contacts,
    });
    setEditing(null);
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="auto-reply-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />Auto-Reply Configuration
          </CardTitle>
          <CardDescription>Set up automatic responses when messages arrive on connected platforms</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {replyPlatforms.map(([id, p]) => {
              const Icon = PLATFORM_ICONS[id] || Plug;
              const colors = PLATFORM_COLORS[id] || 'text-gray-500 bg-gray-500/10';
              const config = autoReplies[id];
              const isEditing = editing === id;
              return (
                <div key={id} className="p-3 rounded-lg border" data-testid={`auto-reply-${id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`h-6 w-6 rounded flex items-center justify-center ${colors}`}><Icon className="h-3.5 w-3.5" /></div>
                      <span className="text-sm font-medium">{p.name}</span>
                      {config?.enabled && <Badge variant="outline" className="text-[10px] text-green-600">Active</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => startEdit(id)} data-testid={`edit-auto-reply-${id}`}>
                        <Settings2 className="h-3 w-3 mr-1" />{config ? 'Edit' : 'Configure'}
                      </Button>
                      {config && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(id)} data-testid={`delete-auto-reply-${id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {config && !isEditing && (
                    <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">"{config.reply_text}"</p>
                  )}
                  {isEditing && (
                    <div className="space-y-2 mt-2 p-3 rounded bg-muted/20 border">
                      <div className="space-y-1">
                        <label className="text-xs font-medium">Auto-reply message</label>
                        <Input value={form.reply_text} onChange={(e) => setForm(f => ({ ...f, reply_text: e.target.value }))}
                          placeholder="Thanks for reaching out! We'll get back to you shortly." className="h-8 text-sm" data-testid={`auto-reply-text-${id}`} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">Trigger keywords (comma-separated, empty = always reply)</label>
                        <Input value={form.trigger_keywords} onChange={(e) => setForm(f => ({ ...f, trigger_keywords: e.target.value }))}
                          placeholder="hello, hi, help, pricing" className="h-8 text-sm" data-testid={`auto-reply-keywords-${id}`} />
                      </div>
                      <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={form.only_new_contacts}
                          onChange={(e) => setForm(f => ({ ...f, only_new_contacts: e.target.checked }))} />
                        Only reply to first-time contacts
                      </label>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs" disabled={!form.reply_text.trim() || saving} onClick={handleSave} data-testid={`save-auto-reply-${id}`}>
                          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Save
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ───
export default function SocialConnectorsPage() {
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState({});
  const [connections, setConnections] = useState({});
  const [messages, setMessages] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [enrichments, setEnrichments] = useState([]);
  const [setupPlatform, setSetupPlatform] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [autoReplies, setAutoReplies] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes, iRes, eRes, cRes, arRes] = await Promise.all([
        api.get('/api/social-connectors/platforms').catch(() => ({ data: {} })),
        api.get('/api/social-connectors/status').catch(() => ({ data: {} })),
        api.get('/api/social-connectors/inbox?limit=30').catch(() => ({ data: {} })),
        api.get('/api/social-connectors/enrichment-history?limit=10').catch(() => ({ data: {} })),
        api.get('/api/social-connectors/conversations?limit=30').catch(() => ({ data: {} })),
        api.get('/api/social-connectors/auto-reply').catch(() => ({ data: {} })),
      ]);
      setPlatforms(pRes.data?.platforms || {});
      setConnections(sRes.data?.connections || {});
      setMessages(iRes.data?.messages || []); setMsgTotal(iRes.data?.total || 0);
      setEnrichments(eRes.data?.enrichments || []);
      setConversations(cRes.data?.conversations || []);
      setAutoReplies(arRes.data?.auto_replies || {});
    } catch (e) {
      toast.error('Failed to load social connectors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConnect = async (platformId, credentials) => {
    setSaving(true);
    try {
      await api.post(`/api/social-connectors/${platformId}/connect`, { credentials });
      toast.success(`${platforms[platformId]?.name} connected!`);
      setSetupPlatform(null);
      await fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error('Connect failed', { description: typeof detail === 'string' ? detail : (detail?.message || 'Unknown error') });
    }
    finally { setSaving(false); }
  };

  const handleTest = async (platformId) => {
    setTesting(platformId);
    try {
      const res = await api.post(`/api/social-connectors/${platformId}/test`);
      res.data.success ? toast.success(res.data.message) : toast.error(res.data.message);
      await fetchData();
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(null); }
  };

  const handleDisconnect = async (platformId) => {
    if (!window.confirm(`Disconnect ${platforms[platformId]?.name}?`)) return;
    try {
      await api.delete(`/api/social-connectors/${platformId}/disconnect`);
      toast.success('Disconnected'); await fetchData();
    } catch (e) { toast.error('Disconnect failed'); }
  };

  const handleReply = async (msgId, text) => {
    try {
      const res = await api.post('/api/social-connectors/inbox/reply', { message_id: msgId, reply_text: text });
      res.data.success ? toast.success('Reply sent') : toast.error(typeof res.data.detail === 'string' ? res.data.detail : (res.data.detail?.message || 'Reply failed'));
      await fetchData();
    } catch (e) { toast.error('Reply failed'); }
  };

  const handleCreateLead = async (msgId) => {
    try {
      const res = await api.post(`/api/social-connectors/inbox/${msgId}/create-lead`);
      toast.success(`Lead created: ${res.data.name}`); await fetchData();
    } catch (e) { toast.error('Failed to create lead'); }
  };

  const handleCreateTicket = async (msgId) => {
    try {
      await api.post(`/api/social-connectors/inbox/${msgId}/create-ticket`);
      toast.success('Support ticket created'); await fetchData();
    } catch (e) { toast.error('Failed to create ticket'); }
  };

  const handleEnrich = async ({ name, email, company }) => {
    try {
      const res = await api.post('/api/social-connectors/enrich', { name, email, company });
      await fetchData();
      return res.data;
    } catch (e) { toast.error('Enrichment failed'); return null; }
  };

  const refreshInbox = async () => {
    setInboxLoading(true);
    try {
      const res = await api.get('/api/social-connectors/inbox?limit=30');
      setMessages(res.data.messages || []); setMsgTotal(res.data.total || 0);
    } catch (e) { /* pass */ }
    finally { setInboxLoading(false); }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    setThreadLoading(true);
    try {
      const res = await api.get(`/api/social-connectors/conversations/${conv.platform}/${encodeURIComponent(conv.contact_id)}?limit=50`);
      setThread(res.data.messages || []);
    } catch (e) { setThread([]); }
    finally { setThreadLoading(false); }
  };

  const refreshConversations = async () => {
    try {
      const res = await api.get('/api/social-connectors/conversations?limit=30');
      setConversations(res.data.conversations || []);
    } catch (e) { /* pass */ }
  };

  const handleSendOutbound = async (platform, recipientId, recipientName, text) => {
    try {
      const res = await api.post('/api/social-connectors/outbound/send', {
        platform, recipient_id: recipientId, recipient_name: recipientName, message_text: text,
      });
      res.data.success ? toast.success('Message sent') : toast.error(res.data.detail || 'Send failed');
      if (selectedConv) await selectConversation(selectedConv);
      await refreshConversations();
    } catch (e) { toast.error('Send failed'); }
  };

  const handleSaveAutoReply = async (config) => {
    try {
      await api.post('/api/social-connectors/auto-reply', config);
      toast.success('Auto-reply saved'); await fetchData();
    } catch (e) { toast.error('Save failed'); }
  };

  const handleDeleteAutoReply = async (platform) => {
    if (!window.confirm('Delete auto-reply for this platform?')) return;
    try {
      await api.delete(`/api/social-connectors/auto-reply/${platform}`);
      toast.success('Auto-reply removed'); await fetchData();
    } catch (e) { toast.error('Delete failed'); }
  };

  const connectedCount = Object.values(connections).filter(c => c.is_connected).length;

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <MessageCircle className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Social Connectors</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
                <div className="space-y-1 flex-1">
                  <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-24 bg-muted/40 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
              <div className="h-8 w-24 bg-muted/60 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="social-connectors-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <MessageCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Social Connectors</h1>
            <p className="text-sm text-muted-foreground">Connect social platforms, receive DMs, capture leads, and enrich data with AI</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="px-3 py-1" data-testid="connected-count">{connectedCount}/{Object.keys(platforms).length} connected</Badge>
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
        </div>
      </div>

      <Tabs defaultValue="platforms" className="space-y-4">
        <TabsList data-testid="social-tabs">
          <TabsTrigger value="platforms" data-testid="tab-platforms"><Plug className="h-4 w-4 mr-1" />Platforms</TabsTrigger>
          <TabsTrigger value="conversations" data-testid="tab-conversations"><MessagesSquare className="h-4 w-4 mr-1" />Conversations</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox"><Inbox className="h-4 w-4 mr-1" />Inbox ({msgTotal})</TabsTrigger>
          <TabsTrigger value="enrichment" data-testid="tab-enrichment"><Brain className="h-4 w-4 mr-1" />Enrichment</TabsTrigger>
          <TabsTrigger value="auto-reply" data-testid="tab-auto-reply"><Bot className="h-4 w-4 mr-1" />Auto-Reply</TabsTrigger>
        </TabsList>

        <TabsContent value="platforms">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="platforms-grid">
            {Object.entries(platforms).map(([id, p]) => (
              <PlatformCard key={id} id={id} platform={p} connection={connections[id]} onSetup={setSetupPlatform} onTest={handleTest} onDisconnect={handleDisconnect} testing={testing} onTemplates={() => setTemplatesOpen(true)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="conversations">
          <ConversationsPanel
            conversations={conversations}
            onSelect={selectConversation}
            onRefresh={refreshConversations}
            loading={loading}
            selectedConv={selectedConv}
            thread={thread}
            threadLoading={threadLoading}
            onSendOutbound={handleSendOutbound}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <UnifiedInbox messages={messages} total={msgTotal} onReply={handleReply} onCreateLead={handleCreateLead} onCreateTicket={handleCreateTicket} onRefresh={refreshInbox} loading={inboxLoading} />
        </TabsContent>

        <TabsContent value="enrichment">
          <EnrichmentPanel onEnrich={handleEnrich} enrichments={enrichments} loading={loading} />
        </TabsContent>

        <TabsContent value="auto-reply">
          <AutoReplyPanel autoReplies={autoReplies} platforms={platforms} onSave={handleSaveAutoReply} onDelete={handleDeleteAutoReply} />
        </TabsContent>
      </Tabs>

      <SetupWizard platformId={setupPlatform} platform={platforms[setupPlatform]} open={!!setupPlatform} onClose={() => setSetupPlatform(null)} onSave={handleConnect} saving={saving} onConnected={fetchData} />
      <WhatsAppTemplatesDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </div>
  );
}
